import { History } from 'app/services/history';
import { EntityStringId } from 'app/types/entity';
import { Option, some, none, fold } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/pipeable';
import { getResponseIds } from 'app/remote/response';
import { request } from 'app/models/request/actions';
import { AppState } from 'app/store';
import { Brand } from 'app/constants/schemas';
import { getEntityOfTypeOrThrow } from 'app/models/entities/selectors';
import { brandRoutes } from 'app/utils/api';
import { AppDispatch } from 'redux';
import { BrandDomain } from 'app/constants/schemas/BrandDomain';
import { ensureBrandReference } from 'app/services/useLoadBrand';
import { parseUrlLoose } from 'app/utils/LooseURL';
import { NotFoundError } from 'app/components/ErrorBoundaryPage';
import { NetworkError } from 'app/constants/ResponseErrors';
import { fetchEntities } from 'app/models/entities/remote';

interface ISiteBrandArgs {
  history: History;
  dispatch: AppDispatch;
  getState: () => AppState;
}

/**
 * Helper for parsing out a single brand id from the response.
 */
const safeGetBrandId = (ids: EntityStringId[]): Option<EntityStringId> => {
  return ids.length === 1 ? some(ids[0]) : none;
};

/**
 * Fetch the brand from the current domain.
 */
export const getSiteBrand = async ({
  history,
  dispatch,
  getState,
}: ISiteBrandArgs): Promise<Brand> => {
  try {
    const brandResponse = await dispatch(fetchEntities({
      fetchAction: request,
      params: brandRoutes.get({ domain: history.location.hostname }),
      type: Brand,
    }));
    const brandIds = getResponseIds(brandResponse);
    brandIds.forEach(bId => dispatch(ensureBrandReference(bId)));
    return pipe(safeGetBrandId(brandIds), fold(
      () => {
        throw new NotFoundError(`The domain ${history.location.hostname} did not match any know brands`);
      },
      (orgId) => {
        return getEntityOfTypeOrThrow(getState(), Brand, orgId);
      }),
    );
  } catch (e) {
    // Check for a CORs error
    if (e instanceof NetworkError && e.status === 0) {
      throw new NotFoundError(`Could not load Brand for ${history.location.hostname}: ${e.message}`);
    } else {
      throw e;
    }
  }
};

export const getMatchedDomain = (state: AppState, brandId: EntityStringId, hostname: string) => {
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const domains = brand.domains.map(dId => getEntityOfTypeOrThrow(state, BrandDomain, dId));
  const matched = domains.find(d => parseUrlLoose(d.envUri).hostname === hostname);

  if (matched === undefined) {
    throw new NotFoundError('Expected for one of the brand\'s domains to match the given hostname');
  }

  return matched;
};
